/**
 * TargetChip — the review row's target (UI-SPEC §2.16 CIBLE, lot F step 14).
 *
 * Chip: `{icon} {name} · {category}`, dashed grey « cible non détectée », or grey
 * « recherche en échec, réessayer » (`targetLookup: 'failed'`, never amber).
 * Click → TargetPicker: no free typing; opens with NO list (the current target row,
 * « Retirer la cible », the focused input); typing searches the brand's Cibles
 * (GET /brands/:b/targets/search, 600 ms debounce, sequence guard), kept: own rows
 * whose category is selectable (any own marque, a sub-brand included, owner 2026-11-02).
 */
import { useEffect, useRef, useState } from 'react';
import { Box, CircularProgress, TextField } from '@mui/material';
import FingerprintIcon from '@mui/icons-material/Fingerprint';
import CategoryIcon from '@mui/icons-material/Category';
import BusinessIcon from '@mui/icons-material/Business';
import FaceIcon from '@mui/icons-material/Face';
import CloseIcon from '@mui/icons-material/Close';
import { api } from '../../services/api';

export interface PickedTarget {
  targetId: string;
  category: string;
  label: string;
}

export interface TargetChipProps {
  brandId: string;
  target: PickedTarget | null;
  targetLookup?: 'failed';
  onChange: (target: PickedTarget | null) => void;
  onRetryDetection?: () => void;
  disabled?: boolean;
}

interface SearchRow {
  targetId: string;
  ownerId: string;
  category: string;
  label: string;
}

const CATEGORY_WORD: Record<string, string> = {
  // i18n: promptStudio.target.category.*
  marque: 'marque',
  etablissement: 'établissement',
  personne: 'personne',
  produit: 'produit',
};
/** Marque, établissement, personne only (owner 2026-11-02); mirrors the API's `SELECTABLE_CATEGORIES`. A stored produit target still renders. */
const SELECTABLE = new Set(['marque', 'etablissement', 'personne']);

/** Own rows of a selectable category. */
export function selectableResults(rows: SearchRow[]): SearchRow[] {
  return rows.filter((r) => r.ownerId === 'own' && SELECTABLE.has(r.category));
}

function TargetIcon({ category, size = 14 }: { category: string; size?: number }) {
  const sx = { fontSize: size, flexShrink: 0 };
  switch (category) {
    case 'marque':        return <FingerprintIcon sx={{ ...sx, color: '#7C3AED' }} />;
    case 'etablissement': return <BusinessIcon sx={{ ...sx, color: '#059669' }} />;
    case 'personne':      return <FaceIcon sx={{ ...sx, color: '#D97706' }} />;
    default:              return <CategoryIcon sx={{ ...sx, color: '#0D9488' }} />;
  }
}

const rowSx = {
  display: 'flex', alignItems: 'center', gap: 1.5, width: '100%', px: 2, py: 1, fontSize: 13, textAlign: 'left',
  bgcolor: 'transparent', border: 'none', cursor: 'pointer', '&:hover': { bgcolor: 'rgba(0,0,0,.04)' },
} as const;

function TargetPicker({
  brandId,
  current,
  onSelect,
  onClose,
}: {
  brandId: string;
  current: PickedTarget | null;
  onSelect: (t: PickedTarget | null) => void;
  onClose: () => void;
}) {
  const overlayRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const seqRef = useRef(0);
  const [q, setQ] = useState('');
  const [results, setResults] = useState<SearchRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [failed, setFailed] = useState(false);

  useEffect(() => { inputRef.current?.focus(); }, []);
  useEffect(() => {
    const h = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', h);
    return () => document.removeEventListener('keydown', h);
  }, [onClose]);

  useEffect(() => {
    const seq = ++seqRef.current;
    const text = q.trim();
    if (!text) {
      setResults([]);
      setLoading(false);
      setFailed(false);
      return;
    }
    setLoading(true);
    const timer = setTimeout(() => {
      api
        .get<{ results: SearchRow[] }>(
          `/brands/${brandId}/targets/search?q=${encodeURIComponent(text)}&categories=marque,etablissement,personne`,
        )
        .then(
          (r) => {
            if (seq !== seqRef.current) return;
            setResults(selectableResults(r?.results ?? []));
            setFailed(false);
          },
          () => {
            if (seq === seqRef.current) setFailed(true);
          },
        )
        .finally(() => {
          if (seq === seqRef.current) setLoading(false);
        });
    }, 600);
    return () => clearTimeout(timer);
  }, [q, brandId]);

  return (
    <Box
      ref={overlayRef}
      onClick={(e) => { e.stopPropagation(); if (e.target === overlayRef.current) onClose(); }}
      sx={{ position: 'fixed', inset: 0, bgcolor: 'rgba(11,11,15,.3)', zIndex: 2000, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
    >
      <Box sx={{ bgcolor: 'background.paper', borderRadius: 2, boxShadow: '0 8px 32px rgba(0,0,0,.18)', width: 320, overflow: 'hidden' }}>
        <Box sx={{ p: 1.5, borderBottom: '1px solid', borderColor: 'divider' }}>
          <TextField
            inputRef={inputRef}
            size="small"
            fullWidth
            placeholder="Chercher une cible" // i18n: promptStudio.targetChip.searchTarget
            value={q}
            onChange={(e) => setQ(e.target.value)}
            InputProps={{ endAdornment: loading ? <CircularProgress size={14} /> : undefined }}
            sx={{ '& .MuiInputBase-root': { fontSize: 13 } }}
          />
        </Box>
        <Box sx={{ maxHeight: 300, overflowY: 'auto' }}>
          {current && !q.trim() && (
            <Box sx={{ ...rowSx, cursor: 'default', bgcolor: 'rgba(124,58,237,.06)', '&:hover': {} }}>
              <TargetIcon category={current.category} size={15} />
              <Box sx={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{current.label}</Box>
              <Box sx={{ fontSize: 11, color: 'text.disabled' }}>{CATEGORY_WORD[current.category] ?? current.category}</Box>
            </Box>
          )}
          {results.map((r) => (
            <Box
              key={r.targetId}
              component="button"
              onClick={() => onSelect({ targetId: r.targetId, category: r.category, label: r.label })}
              sx={rowSx}
            >
              <TargetIcon category={r.category} size={15} />
              <Box sx={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{r.label}</Box>
              <Box sx={{ fontSize: 11, color: 'text.disabled' }}>{CATEGORY_WORD[r.category]}</Box>
            </Box>
          ))}
          {q.trim() && !loading && !failed && results.length === 0 && (
            <Box sx={{ px: 2, py: 1, fontSize: 12, color: 'text.disabled' }}>
              {/* i18n: promptStudio.targetChip.noMatch */}
              Aucune cible ne correspond
            </Box>
          )}
          {failed && (
            <Box sx={{ px: 2, py: 1, fontSize: 12, color: 'text.secondary' }}>
              {/* i18n: promptStudio.targetChip.searchFailed */}
              Recherche indisponible
            </Box>
          )}
          <Box
            component="button"
            onClick={() => onSelect(null)}
            sx={{ ...rowSx, color: 'text.secondary', borderTop: '1px solid', borderColor: 'divider' }}
          >
            <CloseIcon sx={{ fontSize: 14, flexShrink: 0 }} />
            {/* i18n: promptStudio.targetChip.removeTarget */}
            Retirer la cible
          </Box>
        </Box>
      </Box>
    </Box>
  );
}

const chipSx = {
  display: 'inline-flex', alignItems: 'center', gap: 0.5, height: 22, px: 1, borderRadius: '6px', fontSize: 12,
  maxWidth: 280, overflow: 'hidden', whiteSpace: 'nowrap',
} as const;

export function TargetChip({ brandId, target, targetLookup, onChange, onRetryDetection, disabled }: TargetChipProps) {
  const [open, setOpen] = useState(false);

  if (targetLookup === 'failed' && !target) {
    return (
      <Box
        component="button"
        onClick={() => !disabled && onRetryDetection?.()}
        disabled={disabled}
        sx={{ ...chipSx, border: '1px dashed', borderColor: 'divider', bgcolor: 'transparent', color: 'text.secondary', cursor: disabled ? 'default' : 'pointer' }}
      >
        {/* i18n: promptStudio.targetChip.detectionFailed */}
        recherche en échec, réessayer
      </Box>
    );
  }

  return (
    <>
      <Box
        component="button"
        onClick={() => !disabled && setOpen(true)}
        disabled={disabled}
        title={target ? undefined : 'aucune cible trouvée dans les Cibles de la marque'} // i18n: promptStudio.targetChip.noneTitle
        sx={target
          ? { ...chipSx, border: '1px solid', borderColor: 'divider', bgcolor: 'background.paper', color: 'text.primary', cursor: disabled ? 'default' : 'pointer' }
          : { ...chipSx, border: '1px dashed', borderColor: 'divider', bgcolor: 'transparent', color: 'text.disabled', cursor: disabled ? 'default' : 'pointer' }}
      >
        {target ? (
          <>
            <TargetIcon category={target.category} size={13} />
            <Box component="span" sx={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>{target.label}</Box>
            <Box component="span" sx={{ fontSize: 10.5, color: 'text.disabled' }}>· {CATEGORY_WORD[target.category] ?? target.category}</Box>
          </>
        ) : (
          'cible non détectée' // i18n: promptStudio.targetChip.none
        )}
      </Box>
      {open && (
        <TargetPicker
          brandId={brandId}
          current={target}
          onSelect={(t) => { setOpen(false); onChange(t); }}
          onClose={() => setOpen(false)}
        />
      )}
    </>
  );
}
